import * as crypto from "node:crypto";

import { CliError, EXIT_INPUT, EXIT_RUNTIME } from "../common.js";
import {
  aggregateFailures,
  classificationOf,
  contextOf,
  createOperationalFailure,
  formatFailure,
  type OperationalFailure,
} from "../failures.js";
import { AnthropicModel } from "../infra/llm/anthropic-model.js";
import {
  DoubaoModel,
  type DoubaoRunRequest,
} from "../infra/llm/doubao-model.js";
import { GeminiVideoModel } from "../infra/llm/gemini-video-model.js";
import { OpenAiCompatibleModel } from "../infra/llm/openai-compatible-model.js";
import {
  loadLlmConfig,
  normalizeProviderBaseUrl,
  type LlmModelConfig,
  type LlmProviderConfig,
  type ResolvedLlmConfig,
} from "./config.js";
import { LLM_TASK_IDS, LLM_TASK_REGISTRY } from "./registry.js";
import { LlmTransportError } from "./types.js";
import type {
  LlmAdapter,
  LlmExecutorOptions,
  LlmRouteInfo,
  LlmRouteUsage,
  LlmTaskExecutor,
  LlmTaskId,
  LlmUsageEvent,
  TaskVideoPort,
  TextGenerationRequest,
  TextGenerationResponse,
  ToolGenerationRequest,
  ToolGenerationResponse,
  VideoUnderstandingPort,
  VideoGenerationResponse,
  TokenUsage,
} from "./types.js";

export interface ResolvedRoute {
  info: LlmRouteInfo;
  providerConfig: LlmProviderConfig;
  modelConfig: LlmModelConfig;
}

export interface DoubaoVideoRunner {
  run(request: DoubaoRunRequest): Promise<VideoGenerationResponse>;
}

// Keep every built-in Gemini attempt on the current stable Flash model. A custom
// routed model may fall back once to this model before the cross-provider Doubao
// fallback. The Gemini Files handle is reused; only generateContent reruns.
const VIDEO_FALLBACK_MODELS = ["gemini-3.7-flash"] as const;

// Empty while automatic discount-channel routing is paused; explicit overrides remain available.
const PROXY_GEMINI_MODELS: readonly string[] = [];
const PROXY_GEMINI_API_KEY_ENVS = ["GEMINI_PROXY_API_KEY", "REDRAW_GEMINI_PROXY_API_KEY"] as const;
const PROXY_GEMINI_BASE_URL_ENVS = ["GEMINI_PROXY_BASE_URL", "REDRAW_GEMINI_PROXY_BASE_URL"] as const;

// Cross-provider last resort after every Gemini model fails: Doubao on Volcengine
// Ark. Different protocol (Files API sets fps at upload; Responses API references
// the file id), so it re-uploads the local transcode to its own storage. Credential
// is ARK_API_KEY; absent → the fallback is silently skipped, Gemini stays the only path.
// `model` is an Ark inference-endpoint id (ep-…), which resolves server-side to a
// video-understanding model. It's account-specific, so ARK_MODEL overrides the default.
const DOUBAO_FALLBACK = {
  model: "ep-20260721150136-98dj9",
  modelEnv: "ARK_MODEL",
  baseUrl: "https://ark.cn-beijing.volces.com/api/v3",
  apiKeyEnv: "ARK_API_KEY",
  timeoutMs: 600_000,
  maxAttempts: 3,
} as const;

function normalizeBaseUrl(value: string | null): string {
  return value?.trim().replace(/\/+$/, "") ?? "anthropic-sdk-default";
}

function routeFingerprint(task: LlmTaskId, provider: LlmProviderConfig, model: LlmModelConfig): string {
  const spec = LLM_TASK_REGISTRY[task];
  const material = JSON.stringify({
    task,
    taskVersion: spec.version,
    protocol: provider.protocol,
    baseUrl: normalizeBaseUrl(provider.baseUrl),
    model: model.model,
    thinkingBudgetTokens: model.thinkingBudgetTokens ?? null,
  });
  return crypto.createHash("sha256").update(material).digest("hex");
}

function configRuntimeError(message: string, required: string[], received: string[]): CliError {
  return new CliError(message, {
    exitCode: EXIT_INPUT,
    errorCode: "LLM_CONFIG_INVALID",
    hint: "Fix the selected LLM configuration and run `scriptctl doctor`.",
    data: { expected: required, actual: received },
  });
}

function credentialError(provider: string): CliError {
  return new CliError("The selected LLM provider credential is not configured.", {
           exitCode: EXIT_RUNTIME,
           errorCode: "LLM_CREDENTIAL_MISSING",
           hint: "Configure the credential and rerun `scriptctl doctor`.",
           data: { expected: ["credential environment variable referenced by the selected provider"], actual: [`provider: ${provider}`, "credential: missing"] },
         });
}

function routedFailure(error: unknown, route: LlmRouteInfo): OperationalFailure {
  if (error instanceof LlmTransportError) {
    return createOperationalFailure(classificationOf(error.failure), {
      ...contextOf(error.failure),
      provider: route.provider,
      model: route.model,
      retryable: error.failure.retryable,
      message: error.failure.message,
    });
  }
  return createOperationalFailure(
    { failure_type: "unknown", failure_code: "unknown" },
    {
      provider: route.provider,
      model: route.model,
      retryable: false,
      message: error instanceof Error ? error.message : String(error),
    },
  );
}

export class ModelRouter implements LlmTaskExecutor {
  private readonly adapters = new Map<string, LlmAdapter>();
  private readonly videoAdapters = new Map<string, VideoUnderstandingPort>();
  private readonly doubaoAdapters = new Map<string, DoubaoModel>();
  private readonly resolvedRoutes = new Map<LlmTaskId, ResolvedRoute>();
  private readonly usageLog: LlmUsageEvent[] = [];

  constructor(
    private readonly config: ResolvedLlmConfig,
    private readonly options: LlmExecutorOptions = {},
  ) {}

  get configSource(): "builtin" | "file" {
    return this.config.source;
  }

  private resolve(task: LlmTaskId): ResolvedRoute {
    const cached = this.resolvedRoutes.get(task);
    if (cached) return cached;
    const spec = LLM_TASK_REGISTRY[task];
    if (spec.mode === "video" && this.options.videoProvider?.provider === "doubao") {
      const resolved = this.resolveDoubao(task, true);
      this.resolvedRoutes.set(task, resolved);
      return resolved;
    }
    const profile = this.config.tasks[task] ?? spec.defaultProfile;
    const modelConfig = this.config.models[profile];
    if (!modelConfig) throw configRuntimeError("The task route references a missing model profile.", [profile], [`task: ${task}`]);
    const providerConfig = this.config.providers[modelConfig.provider];
    if (!providerConfig) {
      throw configRuntimeError("The model profile references a missing provider.", [modelConfig.provider], [`profile: ${profile}`]);
    }
    if (providerConfig.protocol === "openai-compatible" && !providerConfig.baseUrl) {
      throw configRuntimeError(
        "OpenAI-compatible providers require an explicit endpoint — set the provider's baseUrl, or PROJECT_LITELLM_GATEWAY_BASE_URL for the built-in gateway.",
        ["provider.baseUrl"],
        [`provider: ${modelConfig.provider}`],
      );
    }
    if (modelConfig.thinkingBudgetTokens !== undefined && spec.mode === "tool") {
      throw configRuntimeError(
        "Anthropic thinking cannot be combined with a forced tool call.",
        ["a text task or a profile without thinking"],
        [`task: ${task}`, `profile: ${profile}`],
      );
    }
    if (modelConfig.thinkingBudgetTokens !== undefined && modelConfig.thinkingBudgetTokens >= spec.maxOutputTokens) {
      throw configRuntimeError(
        "The thinking token budget must be smaller than the task output budget.",
        [`thinking budget < ${spec.maxOutputTokens}`],
        [`task: ${task}`, `thinking budget: ${modelConfig.thinkingBudgetTokens}`],
      );
    }
    const videoOverrides = spec.mode === "video" ? this.options.videoProvider : undefined;
    const overriddenProvider = videoOverrides?.baseUrl
      ? { ...providerConfig, baseUrl: normalizeProviderBaseUrl(videoOverrides.baseUrl, "--provider-base-url") }
      : providerConfig;
    const overriddenModel = videoOverrides?.model
      ? { ...modelConfig, model: videoOverrides.model.trim() }
      : modelConfig;
    if (videoOverrides?.model !== undefined && !overriddenModel.model) {
      throw configRuntimeError("The video provider model cannot be empty.", ["non-empty --provider-model"], ["model: empty"]);
    }
    const resolved: ResolvedRoute = {
      info: {
        task,
        profile,
        provider: modelConfig.provider,
        protocol: overriddenProvider.protocol,
        baseUrl: overriddenProvider.baseUrl,
        model: overriddenModel.model,
        fingerprint: routeFingerprint(task, overriddenProvider, overriddenModel),
      },
      providerConfig: overriddenProvider,
      modelConfig: overriddenModel,
    };
    this.resolvedRoutes.set(task, resolved);
    return resolved;
  }

  route(task: LlmTaskId): LlmRouteInfo {
    return this.resolve(task).info;
  }

  routes(): LlmRouteInfo[] {
    return LLM_TASK_IDS.map((task) => this.route(task));
  }

  private emptyUsage(): Omit<LlmRouteUsage, keyof LlmRouteInfo> {
    return {
      calls: 0,
      succeeded: 0,
      failed: 0,
      reused: 0,
      attempts: 0,
      durationMs: 0,
      inputTokens: 0,
      outputTokens: 0,
      cachedInputTokens: 0,
      cacheCreationTokens: 0,
    };
  }

  recordReuse(task: LlmTaskId): void {
    const at = new Date().toISOString();
    this.recordEvent(this.route(task), "reused", at, at, 0, {});
  }

  usage(): LlmRouteUsage[] {
    const aggregate = new Map<string, LlmRouteUsage>();
    for (const event of this.usageLog) {
      const key = `${event.task}\u0000${event.provider}\u0000${event.protocol}\u0000${event.model}\u0000${event.fingerprint}`;
      const usage = aggregate.get(key) ?? {
        task: event.task,
        profile: event.profile,
        provider: event.provider,
        protocol: event.protocol,
        baseUrl: event.baseUrl,
        model: event.model,
        fingerprint: event.fingerprint,
        ...this.emptyUsage(),
      };
      if (event.status === "reused") usage.reused += 1;
      else {
        usage.calls += 1;
        usage[event.status] += 1;
        usage.attempts += event.attempts;
        usage.durationMs += event.durationMs;
        usage.inputTokens += event.inputTokens;
        usage.outputTokens += event.outputTokens;
        usage.cachedInputTokens += event.cachedInputTokens;
        usage.cacheCreationTokens += event.cacheCreationTokens;
      }
      aggregate.set(key, usage);
    }
    return [...aggregate.values()];
  }

  usageEvents(): LlmUsageEvent[] {
    return this.usageLog.map((event) => ({ ...event }));
  }

  private recordEvent(
    route: LlmRouteInfo,
    status: LlmUsageEvent["status"],
    startedAt: string,
    endedAt: string,
    durationMs: number,
    usage: TokenUsage,
    attempts = 0,
    errorCode?: LlmUsageEvent["errorCode"],
  ): void {
    this.usageLog.push({
      ...route,
      sequence: this.usageLog.length + 1,
      status,
      attempts,
      startedAt,
      endedAt,
      durationMs,
      inputTokens: usage.inputTokens ?? 0,
      outputTokens: usage.outputTokens ?? 0,
      cachedInputTokens: usage.cachedInputTokens ?? 0,
      cacheCreationTokens: usage.cacheCreationTokens ?? 0,
      ...(errorCode ? { errorCode } : {}),
    });
  }

  private async tracked<T extends { attempts: number; usage?: TokenUsage }>(
    route: LlmRouteInfo,
    operation: () => Promise<T>,
  ): Promise<T> {
    const startedMs = Date.now();
    const startedAt = new Date(startedMs).toISOString();
    try {
      const response = await operation();
      const endedMs = Date.now();
      this.recordEvent(route, "succeeded", startedAt, new Date(endedMs).toISOString(), endedMs - startedMs, response.usage ?? {}, response.attempts);
      return response;
    } catch (error) {
      const endedMs = Date.now();
      const transport = error instanceof LlmTransportError ? error : null;
      this.recordEvent(
        route,
        "failed",
        startedAt,
        new Date(endedMs).toISOString(),
        endedMs - startedMs,
        transport?.usage ?? {},
        transport?.attempts ?? 1,
        transport?.code,
      );
      throw error;
    }
  }

  private videoRoute(route: ResolvedRoute, model: string): LlmRouteInfo {
    const modelConfig = model === route.info.model ? route.modelConfig : { ...route.modelConfig, model };
    return {
      ...route.info,
      model,
      fingerprint: routeFingerprint(route.info.task, route.providerConfig, modelConfig),
    };
  }

  private resolvedVideoRoute(route: ResolvedRoute, model: string): ResolvedRoute {
    const modelConfig = model === route.info.model ? route.modelConfig : { ...route.modelConfig, model };
    return {
      info: this.videoRoute(route, model),
      providerConfig: route.providerConfig,
      modelConfig,
    };
  }

  private proxyVideoRoutes(task: LlmTaskId): ResolvedRoute[] {
    if (this.options.videoProvider) return [];
    const apiKeyEnv = PROXY_GEMINI_API_KEY_ENVS.find((name) => (process.env[name] ?? "").trim());
    const baseUrlEnv = PROXY_GEMINI_BASE_URL_ENVS.find((name) => (process.env[name] ?? "").trim());
    if (!apiKeyEnv || !baseUrlEnv) return [];
    const baseUrl = normalizeProviderBaseUrl(process.env[baseUrlEnv]!, baseUrlEnv);
    return PROXY_GEMINI_MODELS.map((model, index) => {
      const providerConfig: LlmProviderConfig = {
        protocol: "google-gemini",
        baseUrl,
        apiKeyEnv,
        timeoutMs: 90_000,
        maxAttempts: index < 2 ? 2 : 1,
      };
      const modelConfig: LlmModelConfig = { provider: "gemini-proxy", model };
      return {
        info: {
          task,
          profile: "gemini-proxy",
          provider: "gemini-proxy",
          protocol: "google-gemini",
          baseUrl,
          model,
          fingerprint: routeFingerprint(task, providerConfig, modelConfig),
        },
        providerConfig,
        modelConfig,
      };
    });
  }

  private resolveDoubao(task: LlmTaskId, explicit: boolean): ResolvedRoute {
    const overrides = explicit ? this.options.videoProvider : undefined;
    const modelName = explicit
      ? (overrides?.model ?? process.env["ARK_MODEL"] ?? "").trim()
      : this.doubaoModel();
    if (!modelName) {
      throw configRuntimeError("Doubao requires --provider-model or ARK_MODEL.", ["--provider-model <id> or ARK_MODEL"], ["model: empty"]);
    }
    const providerConfig: LlmProviderConfig = {
      protocol: "openai-compatible",
      baseUrl: overrides?.baseUrl
        ? normalizeProviderBaseUrl(overrides.baseUrl, "--provider-base-url")
        : DOUBAO_FALLBACK.baseUrl,
      apiKeyEnv: DOUBAO_FALLBACK.apiKeyEnv,
      timeoutMs: DOUBAO_FALLBACK.timeoutMs,
      maxAttempts: DOUBAO_FALLBACK.maxAttempts,
    };
    const modelConfig: LlmModelConfig = { provider: "doubao", model: modelName };
    return {
      info: {
        task,
        profile: explicit ? "doubao" : "doubao-fallback",
        provider: "doubao",
        protocol: providerConfig.protocol,
        baseUrl: providerConfig.baseUrl,
        model: modelName,
        fingerprint: routeFingerprint(task, providerConfig, modelConfig),
      },
      providerConfig,
      modelConfig,
    };
  }

  private doubaoRoute(task: LlmTaskId): LlmRouteInfo {
    return this.resolveDoubao(task, false).info;
  }

  private adapter(route: ResolvedRoute): LlmAdapter {
    const key = `${route.info.provider}\u0000${route.info.model}\u0000${route.modelConfig.thinkingBudgetTokens ?? ""}`;
    const cached = this.adapters.get(key);
    if (cached) return cached;
    const apiKey = (process.env[route.providerConfig.apiKeyEnv] ?? "").trim();
    if (!apiKey) throw credentialError(route.info.provider);
    const adapter = route.providerConfig.protocol === "anthropic"
      ? new AnthropicModel(route.providerConfig, route.modelConfig, apiKey)
      : new OpenAiCompatibleModel(route.providerConfig, route.modelConfig, apiKey);
    this.adapters.set(key, adapter);
    return adapter;
  }

  // model defaults to the route's model; pass an override to build a peer adapter
  // that shares the provider/credential but targets a fallback model.
  protected videoAdapter(route: ResolvedRoute, model?: string): VideoUnderstandingPort {
    if (route.providerConfig.protocol !== "google-gemini") {
      throw configRuntimeError(
        "Video tasks require a google-gemini provider.",
        ["provider.protocol: google-gemini"],
        [`provider: ${route.info.provider}`, `protocol: ${route.providerConfig.protocol}`],
      );
    }
    const modelName = model ?? route.info.model;
    const key = `${route.info.provider} ${route.providerConfig.baseUrl ?? "official"} ${modelName}`;
    const cached = this.videoAdapters.get(key);
    if (cached) return cached;
    const apiKey = this.videoCredential(route);
    if (!apiKey) throw credentialError(route.info.provider);
    const modelConfig = model ? { ...route.modelConfig, model } : route.modelConfig;
    const adapter = new GeminiVideoModel(route.providerConfig, modelConfig, apiKey);
    this.videoAdapters.set(key, adapter);
    return adapter;
  }

  private videoCredential(route: ResolvedRoute): string {
    const override = this.options.videoProvider?.apiKey;
    const apiKey = override?.kind === "literal"
      ? override.value.trim()
      : override?.kind === "env"
        ? (process.env[override.name] ?? "").trim()
        : (process.env[route.providerConfig.apiKeyEnv] ?? "").trim();
    if (!apiKey) throw credentialError(route.info.provider);
    return apiKey;
  }

  videoPort(task: LlmTaskId): TaskVideoPort {
    const spec = LLM_TASK_REGISTRY[task];
    if (spec.mode !== "video") throw configRuntimeError("The task does not support video generation.", ["video task"], [`task: ${task}`]);
    const route = this.resolve(task);
    if (this.options.videoProvider?.provider === "doubao") {
      const adapter = this.doubaoAdapter(route);
      if (!adapter) throw credentialError("doubao");
      return {
        prepare: async (localPath, mimeType, mode, signal) => {
          if (mode !== "file-api") {
            throw configRuntimeError("Doubao requires file-api upload mode.", ["file-api"], [mode]);
          }
          signal?.throwIfAborted();
          return { kind: "local", path: localPath, mimeType };
        },
        generate: (request) => {
          if (request.media.kind !== "local") {
            throw configRuntimeError("Doubao requires locally prepared video.", ["local media"], [request.media.kind]);
          }
          request.signal?.throwIfAborted();
          const { path: localPath, mimeType: localMime } = request.media;
          return this.tracked(route.info, () => adapter.run({
            localPath,
            localMime,
            user: request.user,
            fps: request.fps,
            maxOutputTokens: spec.maxOutputTokens,
            ...(request.system ? { system: request.system } : {}),
            ...(request.responseJsonSchema ? { responseJsonSchema: request.responseJsonSchema } : {}),
            ...(request.signal ? { signal: request.signal } : {}),
          }));
        },
        release: async () => {},
      };
    }
    if (route.providerConfig.protocol !== "google-gemini") {
      throw configRuntimeError(
        "Video tasks require a google-gemini provider.",
        ["provider.protocol: google-gemini"],
        [`provider: ${route.info.provider}`, `protocol: ${route.providerConfig.protocol}`],
      );
    }
    const officialRoutes = (
      this.options.videoProvider
        ? [route.info.model]
        : [route.info.model, ...VIDEO_FALLBACK_MODELS.filter((m) => m !== route.info.model)]
    ).map((model) => this.resolvedVideoRoute(route, model));
    const proxyRoutes = this.proxyVideoRoutes(task);
    if (this.options.videoProvider || proxyRoutes.length === 0) void this.videoAdapter(route);
    return {
      prepare: async (localPath, mimeType, mode, signal) => {
        const prepareRoute = mode === "base64" && proxyRoutes.length > 0 ? proxyRoutes[0]! : route;
        try {
          return await this.videoAdapter(prepareRoute).prepare(localPath, mimeType, mode, signal);
        } catch (error) {
          if (signal?.aborted || mode !== "file-api" || this.options.videoProvider || !this.doubaoAdapter()) throw error;
          const failure = routedFailure(error, route.info);
          process.stderr.write(`↪️  Gemini 文件上传失败,改由豆包读取本地视频 (${formatFailure(failure)})\n`);
          return { kind: "local-fallback", path: localPath, mimeType, primaryFailure: failure };
        }
      },
      release: (media, signal) => media.kind === "base64" || media.kind === "local-fallback"
        ? Promise.resolve()
        : this.videoAdapter(route).release(media, signal),
      generate: async (request) => {
        const failures: OperationalFailure[] = request.media.kind === "local-fallback"
          ? [request.media.primaryFailure]
          : [];
        const skipped: string[] = [];
        const geminiChain = request.media.kind === "local-fallback"
          ? []
          : request.media.kind === "base64"
            ? [...proxyRoutes, ...officialRoutes]
            : officialRoutes;
        for (const [index, attemptRoute] of geminiChain.entries()) {
          try {
            const response = await this.tracked(
              attemptRoute.info,
              () => this.videoAdapter(attemptRoute).generate({ ...request, maxOutputTokens: spec.maxOutputTokens }),
            );
            if (index > 0) process.stderr.write(`↪️  已切换到备用模型 ${attemptRoute.info.model}\n`);
            return response;
          } catch (error) {
            // Abort is the caller's decision, not a model failure — stop the whole chain.
            if (request.signal?.aborted) throw error;
            const failure = routedFailure(error, attemptRoute.info);
            failures.push(failure);
            const detail = formatFailure(failure);
            if (index < geminiChain.length - 1) {
              process.stderr.write(`↪️  ${attemptRoute.info.model} 当前不可用，切换下一线路 (${detail})\n`);
            }
          }
        }
        // Cross-provider last resort: Doubao (Ark). Needs ARK_API_KEY + the local file.
        const doubao = this.options.videoProvider ? null : this.doubaoAdapter();
        const localPath = request.media.kind === "local-fallback" ? request.media.path : request.localPath;
        if (doubao && localPath) {
          const attemptRoute = this.doubaoRoute(task);
          try {
            process.stderr.write(`↪️  Gemini 全部失败,回退到豆包 ${this.doubaoModel()}\n`);
            const response = await this.tracked(attemptRoute, () => doubao.run({
                localPath,
                localMime: request.media.kind === "local-fallback" ? request.media.mimeType : request.localMime ?? "video/mp4",
                user: request.user,
                fps: request.fps,
                maxOutputTokens: spec.maxOutputTokens,
                ...(request.system ? { system: request.system } : {}),
                ...(request.responseJsonSchema ? { responseJsonSchema: request.responseJsonSchema } : {}),
                ...(request.signal ? { signal: request.signal } : {}),
              }));
            return response;
          } catch (error) {
            if (request.signal?.aborted) throw error;
            failures.push(routedFailure(error, attemptRoute));
          }
        } else if (!doubao) {
          if (!this.options.videoProvider) {
            skipped.push("doubao: skipped (ARK_API_KEY not set)");
          }
        } else {
          skipped.push("doubao: skipped (no local file to re-upload)");
        }
        const aggregate = aggregateFailures(failures, "All configured video model attempts failed.");
        throw new LlmTransportError("request_failed", aggregate.message, {
          retryable: aggregate.retryable,
          classification: classificationOf(aggregate),
          context: contextOf(aggregate),
          modelFailures: failures,
          details: [...failures.map(formatFailure), ...skipped],
        });
      },
    };
  }

  // Resolve an explicit Doubao route or the default fallback; absent fallback credentials skip it.
  // Overridable in tests to inject a fake Ark client.
  protected doubaoAdapter(route?: ResolvedRoute): DoubaoVideoRunner | null {
    return this.arkAdapter(route);
  }

  protected doubaoTextAdapter(): LlmAdapter | null {
    return this.arkAdapter();
  }

  private arkAdapter(route?: ResolvedRoute): DoubaoModel | null {
    const apiKey = route ? this.videoCredential(route) : (process.env[DOUBAO_FALLBACK.apiKeyEnv] ?? "").trim();
    if (!apiKey) return null;
    const model = route?.info.model ?? this.doubaoModel();
    const baseUrl = route?.providerConfig.baseUrl ?? DOUBAO_FALLBACK.baseUrl;
    const key = `doubao ${baseUrl} ${model}`;
    const cached = this.doubaoAdapters.get(key);
    if (cached) return cached;
    const adapter = new DoubaoModel(model, DOUBAO_FALLBACK.timeoutMs, DOUBAO_FALLBACK.maxAttempts, apiKey, baseUrl);
    this.doubaoAdapters.set(key, adapter);
    return adapter;
  }

  // Ark endpoint id / model name, ARK_MODEL overriding the account-specific default.
  protected doubaoModel(): string {
    return (process.env[DOUBAO_FALLBACK.modelEnv] ?? "").trim() || DOUBAO_FALLBACK.model;
  }

  private async withModerationFallback<T extends TextGenerationResponse | ToolGenerationResponse>(
    task: LlmTaskId, signal: AbortSignal | undefined,
    generate: (adapter: LlmAdapter) => Promise<T>,
  ): Promise<T> {
    const route = this.resolve(task);
    const adapter = this.adapter(route);
    try {
      return await this.tracked(route.info, () => generate(adapter));
    } catch (error) {
      if (signal?.aborted || !(error instanceof LlmTransportError) || error.code !== "content_filtered") throw error;
      const doubao = this.doubaoTextAdapter();
      if (!doubao) throw error;
      process.stderr.write(`↪️  文本内容审核拒绝，回退到豆包 ${this.doubaoModel()}\n`);
      return this.tracked(this.doubaoRoute(task), () => generate(doubao));
    }
  }

  async generateText(task: LlmTaskId, request: Omit<TextGenerationRequest, "maxOutputTokens">): Promise<TextGenerationResponse> {
    const spec = LLM_TASK_REGISTRY[task];
    if (spec.mode !== "text") throw configRuntimeError("The task does not support text generation.", ["text task"], [`task: ${task}`]);
    return this.withModerationFallback(task, request.signal, (adapter) => adapter.generateText({ ...request, maxOutputTokens: spec.maxOutputTokens }));
  }

  async generateTool(task: LlmTaskId, request: Omit<ToolGenerationRequest, "maxOutputTokens">): Promise<ToolGenerationResponse> {
    const spec = LLM_TASK_REGISTRY[task];
    if (spec.mode !== "tool") throw configRuntimeError("The task does not support tool generation.", ["tool task"], [`task: ${task}`]);
    return this.withModerationFallback(task, request.signal, (adapter) => adapter.generateTool({ ...request, maxOutputTokens: spec.maxOutputTokens }));
  }
}

export function createLlmExecutor(explicitConfigPath?: string, options?: LlmExecutorOptions): ModelRouter {
  return new ModelRouter(loadLlmConfig(explicitConfigPath), options);
}
